import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { projectAPI, bugAPI, shareLinkAPI } from '../services/api';
import type { ProjectShareLinkSummary, CreateShareResponse } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { Project, Bug, BugStats } from '../types';

interface BugImage {
  id: string;
  name: string;
  url: string;
  file?: File;
}

const ProjectDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [project, setProject] = useState<Project | null>(null);
  const [bugs, setBugs] = useState<Bug[]>([]);
  const [stats, setStats] = useState<BugStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImages, setSelectedImages] = useState<string[]>([]);
  const [newBug, setNewBug] = useState({
    type: '',
    location: '',
    content: '',
    remark: '',
  });
  const [bugImages, setBugImages] = useState<BugImage[]>([]);
  const [editProject, setEditProject] = useState({
    name: '',
    description: '',
    bugTypes: [] as string[],
    bugLocations: [] as string[],
  });
  // 分页状态
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalBugs, setTotalBugs] = useState(0);
  const pageSize = 15;
  const shareLinksPageSize = 10;
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareLinks, setShareLinks] = useState<ProjectShareLinkSummary[]>([]);
  const [shareLinksPage, setShareLinksPage] = useState(1);
  const [shareLoading, setShareLoading] = useState(false);
  const [lastCreatedShare, setLastCreatedShare] = useState<CreateShareResponse | null>(null);
  const { isAdmin, user } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const imageUploadRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (id) {
      fetchProjectData(1);
    }
  }, [id]);

  // 处理页码变化
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    fetchProjectData(page);
  };

  // 处理剪切板粘贴事件
  useEffect(() => {
    const handlePaste = (e: ClipboardEvent) => {
      if (!showCreateForm) return;
      
      const items = e.clipboardData?.items;
      if (!items) return;

      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.type.indexOf('image') !== -1) {
          const file = item.getAsFile();
          if (file) {
            handleImageUpload(file);
          }
          break;
        }
      }
    };

    document.addEventListener('paste', handlePaste);
    return () => {
      document.removeEventListener('paste', handlePaste);
    };
  }, [showCreateForm]);

  const fetchProjectData = async (page: number = 1) => {
    try {
      const [projectData, bugsData, statsData] = await Promise.all([
        projectAPI.getById(id!),
        bugAPI.getByProject(id!, page, pageSize),
        bugAPI.getStats(id!),
      ]);
      setProject(projectData);
      setBugs(bugsData.bugs);
      setTotalPages(bugsData.totalPages);
      setTotalBugs(bugsData.total);
      setCurrentPage(bugsData.currentPage);
      setStats(statsData);
      // 初始化编辑表单
      setEditProject({
        name: projectData.name,
        description: projectData.description,
        bugTypes: [...projectData.bugTypes],
        bugLocations: [...projectData.bugLocations],
      });
    } catch (err: any) {
      setError(err.response?.data?.message || '获取项目数据失败');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateBug = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      // 创建FormData对象来处理文件上传
      const formData = new FormData();
      formData.append('projectId', id!);
      formData.append('type', newBug.type);
      formData.append('location', newBug.location);
      formData.append('content', newBug.content);
      formData.append('remark', newBug.remark);
      
      // 添加图片文件
      bugImages.forEach((image, index) => {
        if (image.file) {
          formData.append(`images`, image.file);
        }
      });

      await bugAPI.createWithImages(formData);
      closeCreateBugModal();
      fetchProjectData(1); // 重新获取数据，回到第一页
    } catch (err: any) {
      setError(err.response?.data?.message || '创建BUG失败');
    }
  };

  const handleUpdateProject = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await projectAPI.update(id!, editProject);
      setShowEditForm(false);
      fetchProjectData(currentPage); // 重新获取数据，保持当前页
    } catch (err: any) {
      setError(err.response?.data?.message || '更新项目失败');
    }
  };

  const handleStatusChange = async (bugId: string, status: 'pending' | 'fixed' | 'shelved', shelvedReason?: string) => {
    try {
      let updateData: any = { status };
      if (status === 'fixed') {
        updateData.fixedByName = user?.realName || '';
      }
      if (status === 'shelved') {
        updateData.fixedByName = user?.realName || '';
        updateData.shelvedReason = shelvedReason;
      }
      await bugAPI.updateStatus(bugId, updateData);
      fetchProjectData(currentPage); // 重新获取数据，保持当前页
    } catch (err: any) {
      setError(err.response?.data?.message || '更新状态失败');
    }
  };

  const handleDeleteBug = async (bugId: string) => {
    if (window.confirm('确定要删除这个BUG吗？此操作不可恢复。')) {
      try {
        await bugAPI.delete(bugId);
        fetchProjectData(currentPage); // 重新获取数据，保持当前页
      } catch (err: any) {
        setError(err.response?.data?.message || '删除BUG失败');
      }
    }
  };

  const openShareModal = async () => {
    if (!id) return;
    setShowShareModal(true);
    setLastCreatedShare(null);
    setShareLinksPage(1);
    setShareLoading(true);
    try {
      const list = await shareLinkAPI.listByProject(id);
      setShareLinks(list);
    } catch (err: any) {
      setError(err.response?.data?.message || '加载分享列表失败');
    } finally {
      setShareLoading(false);
    }
  };

  const handleCreateShareLink = async () => {
    if (!id) return;
    try {
      const created = await shareLinkAPI.create(id);
      setLastCreatedShare(created);
      const list = await shareLinkAPI.listByProject(id);
      setShareLinks(list);
    } catch (err: any) {
      setError(err.response?.data?.message || '创建分享失败');
    }
  };

  const handleRevokeShare = async (shareId: string) => {
    if (!id) return;
    if (!window.confirm('确定作废该分享链接？客户将无法再使用此链接。')) return;
    try {
      await shareLinkAPI.revoke(shareId, id);
      const list = await shareLinkAPI.listByProject(id);
      setShareLinks(list);
    } catch (err: any) {
      setError(err.response?.data?.message || '作废失败');
    }
  };

  const copyShareClipboard = async (token: string, pin?: string) => {
    if (!project) {
      setError('项目信息尚未加载完成，请稍后再试复制');
      return;
    }
    const url = `${window.location.origin}/share/${token}`;
    const pinPart = (pin ?? '').trim() || '无记录';
    const text = `【${project.name}】产品缺陷反馈地址为：【${url}】，分享码：【${pinPart}】。`;

    const legacyCopy = () => {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.setAttribute('readonly', '');
      ta.style.position = 'fixed';
      ta.style.left = '-9999px';
      document.body.appendChild(ta);
      ta.select();
      ta.setSelectionRange(0, text.length);
      try {
        document.execCommand('copy');
      } finally {
        document.body.removeChild(ta);
      }
    };

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        return;
      }
    } catch {
      /* 部分环境无 Clipboard API 或权限被拒，走降级 */
    }
    try {
      legacyCopy();
    } catch {
      window.prompt('请复制以下内容：', text);
    }
  };

  const shareLinksTotalPages = Math.max(1, Math.ceil(shareLinks.length / shareLinksPageSize));
  const pagedShareLinks = useMemo(
    () =>
      shareLinks.slice(
        (shareLinksPage - 1) * shareLinksPageSize,
        shareLinksPage * shareLinksPageSize
      ),
    [shareLinks, shareLinksPage, shareLinksPageSize]
  );

  useEffect(() => {
    if (shareLinksPage > shareLinksTotalPages) {
      setShareLinksPage(shareLinksTotalPages);
    }
  }, [shareLinksPage, shareLinksTotalPages]);

  const handleViewImages = (images: string[]) => {
    setSelectedImages(images);
    setShowImageModal(true);
  };

  const handleImageUpload = (file: File) => {
    // 验证文件类型
    if (!file.type.startsWith('image/')) {
      setError('请选择图片文件');
      return;
    }

    // 验证文件大小（限制为5MB）
    if (file.size > 5 * 1024 * 1024) {
      setError('图片文件大小不能超过5MB');
      return;
    }

    // 创建预览URL
    const imageUrl = URL.createObjectURL(file);
    const imageId = Date.now().toString();
    
    const newImage: BugImage = {
      id: imageId,
      name: file.name,
      url: imageUrl,
      file: file
    };

    setBugImages(prev => [...prev, newImage]);
  };

  const removeImage = (imageId: string) => {
    setBugImages(prev => {
      const image = prev.find(img => img.id === imageId);
      if (image && image.url.startsWith('blob:')) {
        URL.revokeObjectURL(image.url);
      }
      return prev.filter(img => img.id !== imageId);
    });
  };

  const closeCreateBugModal = () => {
    setBugImages((prev) => {
      prev.forEach((img) => {
        if (img.url.startsWith('blob:')) {
          URL.revokeObjectURL(img.url);
        }
      });
      return [];
    });
    setNewBug({ type: '', location: '', content: '', remark: '' });
    setShowCreateForm(false);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files) {
      Array.from(files).forEach(handleImageUpload);
    }
    // 清空input值，允许重复选择同一文件
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const addBugType = () => {
    const newType = prompt('请输入新的BUG类型:');
    if (newType && newType.trim()) {
      setEditProject({
        ...editProject,
        bugTypes: [...editProject.bugTypes, newType.trim()]
      });
    }
  };

  const removeBugType = (index: number) => {
    const newTypes = editProject.bugTypes.filter((_, i) => i !== index);
    setEditProject({
      ...editProject,
      bugTypes: newTypes
    });
  };

  const addBugLocation = () => {
    const newLocation = prompt('请输入新的BUG位置:');
    if (newLocation && newLocation.trim()) {
      setEditProject({
        ...editProject,
        bugLocations: [...editProject.bugLocations, newLocation.trim()]
      });
    }
  };

  const removeBugLocation = (index: number) => {
    const newLocations = editProject.bugLocations.filter((_, i) => i !== index);
    setEditProject({
      ...editProject,
      bugLocations: newLocations
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">加载中...</p>
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">项目不存在</h2>
          <Link to="/dashboard" className="text-indigo-600 hover:text-indigo-500">
            返回仪表板
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <nav className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center">
              <Link to="/dashboard" className="text-indigo-600 hover:text-indigo-500 mr-4">
                ← 返回
              </Link>
              <h1 className="text-xl font-semibold text-gray-900">{project.name}</h1>
            </div>
            <div className="flex items-center space-x-3">
              <button
                type="button"
                onClick={openShareModal}
                className="bg-white border border-gray-300 hover:bg-gray-50 text-gray-800 px-4 py-2 rounded-md text-sm font-medium"
              >
                分享
              </button>
              <button
                onClick={() => setShowCreateForm(true)}
                className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-md text-sm font-medium"
              >
                添加BUG
              </button>
              {isAdmin && (
                <button
                  onClick={() => setShowEditForm(true)}
                  className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md text-sm font-medium"
                >
                  编辑项目
                </button>
              )}
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
              {error}
            </div>
          )}

          <div className="bg-white shadow rounded-lg p-6 min-h-[72vh]">
            <h2 className="text-lg font-medium text-gray-900 mb-4">项目详情</h2>
            <p className="text-gray-600 mb-4">{project.description || '暂无描述'}</p>
            
            {stats && (
              <div className="grid grid-cols-4 gap-4 mb-6">
                <div className="text-center">
                  <div className="text-2xl font-bold text-gray-900">{stats.total}</div>
                  <div className="text-sm text-gray-500">总计</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-yellow-600">{stats.pending}</div>
                  <div className="text-sm text-gray-500">未修改</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-600">{stats.fixed}</div>
                  <div className="text-sm text-gray-500">已修改</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-gray-600">{stats.shelved}</div>
                  <div className="text-sm text-gray-500">搁置</div>
                </div>
              </div>
            )}

            {/* 编辑项目表单 */}
            {showEditForm && isAdmin && (
              <div className="mb-6 bg-gray-50 p-4 rounded-lg">
                <h3 className="text-lg font-medium text-gray-900 mb-4">编辑项目配置</h3>
                <form onSubmit={handleUpdateProject} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      项目名称
                    </label>
                    <input
                      type="text"
                      value={editProject.name}
                      onChange={(e) => setEditProject({ ...editProject, name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      项目描述
                    </label>
                    <textarea
                      value={editProject.description}
                      onChange={(e) => setEditProject({ ...editProject, description: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      rows={3}
                    />
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <div className="flex justify-between items-center mb-2">
                        <label className="block text-sm font-medium text-gray-700">
                          BUG类型
                        </label>
                        <button
                          type="button"
                          onClick={addBugType}
                          className="text-indigo-600 hover:text-indigo-500 text-sm"
                        >
                          + 添加类型
                        </button>
                      </div>
                      <div className="space-y-2">
                        {editProject.bugTypes.map((type, index) => (
                          <div key={index} className="flex items-center space-x-2">
                            <input
                              type="text"
                              value={type}
                              onChange={(e) => {
                                const newTypes = [...editProject.bugTypes];
                                newTypes[index] = e.target.value;
                                setEditProject({ ...editProject, bugTypes: newTypes });
                              }}
                              className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            />
                            <button
                              type="button"
                              onClick={() => removeBugType(index)}
                              className="text-red-600 hover:text-red-800 text-sm"
                            >
                              删除
                            </button>
                          </div>
                        ))}
                        {editProject.bugTypes.length === 0 && (
                          <p className="text-gray-500 text-sm">暂无BUG类型，请添加</p>
                        )}
                      </div>
                    </div>

                    <div>
                      <div className="flex justify-between items-center mb-2">
                        <label className="block text-sm font-medium text-gray-700">
                          BUG位置
                        </label>
                        <button
                          type="button"
                          onClick={addBugLocation}
                          className="text-indigo-600 hover:text-indigo-500 text-sm"
                        >
                          + 添加位置
                        </button>
                      </div>
                      <div className="space-y-2">
                        {editProject.bugLocations.map((location, index) => (
                          <div key={index} className="flex items-center space-x-2">
                            <input
                              type="text"
                              value={location}
                              onChange={(e) => {
                                const newLocations = [...editProject.bugLocations];
                                newLocations[index] = e.target.value;
                                setEditProject({ ...editProject, bugLocations: newLocations });
                              }}
                              className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            />
                            <button
                              type="button"
                              onClick={() => removeBugLocation(index)}
                              className="text-red-600 hover:text-red-800 text-sm"
                            >
                              删除
                            </button>
                          </div>
                        ))}
                        {editProject.bugLocations.length === 0 && (
                          <p className="text-gray-500 text-sm">暂无BUG位置，请添加</p>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-end space-x-3">
                    <button
                      type="button"
                      onClick={() => setShowEditForm(false)}
                      className="px-4 py-2 text-gray-700 bg-gray-200 hover:bg-gray-300 rounded-md text-sm font-medium"
                    >
                      取消
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md text-sm font-medium"
                    >
                      保存修改
                    </button>
                  </div>
                </form>
              </div>
            )}

            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase w-16">序号</th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase w-24">类型</th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase w-24">位置</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">内容</th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase w-20">测试人</th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase w-20">状态</th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase w-48">操作</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {bugs.map((bug) => (
                  <tr key={bug.id}>
                    <td className="px-3 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      #{bug.serialNumber}
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
                      {bug.type}
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
                      {bug.location}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      <div>
                        <div>{bug.content}</div>
                        {bug.remark && (
                          <div className="text-gray-500 text-xs mt-1">备注: {bug.remark}</div>
                        )}
                        {bug.images && bug.images.length > 0 && (
                          <button
                            onClick={() => handleViewImages(bug.images!)}
                            className="mt-2 text-indigo-600 hover:text-indigo-800 text-xs font-medium"
                          >
                            📷 查看图片 ({bug.images.length})
                          </button>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
                      {bug.creatorName || '未知用户'}
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap">
                      <div className="relative group inline-block">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            bug.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                            bug.status === 'fixed' ? 'bg-green-100 text-green-800' :
                            'bg-gray-100 text-gray-800'
                          }`}
                        >
                          {bug.status === 'pending' ? '未修改' :
                            bug.status === 'fixed' ? '已修改' : '搁置'}
                        </span>
                        {/* 自定义Tooltip，已修改显示fixedByName，搁置显示shelvedReason */}
                        {bug.status === 'fixed' && bug.fixedByName && (
                          <div className="absolute left-1/2 z-20 -translate-x-1/2 mt-2 w-max max-w-xs px-3 py-2 bg-white text-gray-700 text-xs rounded-xl shadow-lg border border-gray-200 opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-200 whitespace-pre-line">
                            {bug.fixedByName}
                          </div>
                        )}
                        {bug.status === 'shelved' && bug.shelvedReason && (
                          <div className="absolute left-1/2 z-20 -translate-x-1/2 mt-2 w-max max-w-xs px-3 py-2 bg-white text-gray-700 text-xs rounded-xl shadow-lg border border-gray-200 opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-200 whitespace-pre-line">
                            {bug.shelvedReason}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div className="flex flex-row flex-wrap gap-2 items-center">
                        {bug.status === 'pending' && (
                          <>
                            <button
                              onClick={() => handleStatusChange(bug.id, 'fixed')}
                              className="text-green-600 hover:text-green-900 text-xs"
                            >
                              标记已修改
                            </button>
                            <button
                              onClick={() => {
                                const reason = prompt('请输入搁置原因:');
                                if (reason !== null) {
                                  handleStatusChange(bug.id, 'shelved', reason);
                                }
                              }}
                              className="text-gray-600 hover:text-gray-900 text-xs"
                            >
                              搁置
                            </button>
                          </>
                        )}
                        {bug.status === 'shelved' && (
                          <button
                            onClick={() => handleStatusChange(bug.id, 'pending')}
                            className="text-yellow-600 hover:text-yellow-900 text-xs"
                            title={bug.shelvedReason ? `搁置备注：${bug.shelvedReason}` : ''}
                          >
                            恢复
                          </button>
                        )}
                        {isAdmin && (
                          <button
                            onClick={() => handleDeleteBug(bug.id)}
                            className="text-red-600 hover:text-red-900 text-xs"
                          >
                            删除
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {bugs.length === 0 && (
              <div className="text-center py-12">
                <p className="text-gray-500">暂无BUG记录</p>
              </div>
            )}

            {/* 分页组件 */}
            {totalPages > 1 && (
              <div className="mt-6 flex items-center justify-between">
                <div className="text-sm text-gray-700">
                  显示第 {((currentPage - 1) * pageSize) + 1} 到 {Math.min(currentPage * pageSize, totalBugs)} 条，共 {totalBugs} 条记录
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className={`px-3 py-2 text-sm font-medium rounded-md ${
                      currentPage === 1
                        ? 'text-gray-400 bg-gray-100 cursor-not-allowed'
                        : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    上一页
                  </button>
                  
                  {/* 页码按钮 */}
                  <div className="flex items-center space-x-1">
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum: number;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }
                      
                      return (
                        <button
                          key={pageNum}
                          onClick={() => handlePageChange(pageNum)}
                          className={`px-3 py-2 text-sm font-medium rounded-md ${
                            currentPage === pageNum
                              ? 'bg-indigo-600 text-white'
                              : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                  </div>
                  
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className={`px-3 py-2 text-sm font-medium rounded-md ${
                      currentPage === totalPages
                        ? 'text-gray-400 bg-gray-100 cursor-not-allowed'
                        : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    下一页
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 添加 BUG 弹窗 */}
      {showCreateForm && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black bg-opacity-50"
          role="dialog"
          aria-modal="true"
          aria-labelledby="create-bug-dialog-title"
          onClick={closeCreateBugModal}
        >
          <div
            className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex-shrink-0 flex justify-between items-center px-6 py-4 border-b border-gray-200">
              <h3 id="create-bug-dialog-title" className="text-lg font-medium text-gray-900">
                添加新BUG
              </h3>
              <button
                type="button"
                onClick={closeCreateBugModal}
                className="text-gray-400 hover:text-gray-600 text-xl leading-none"
                aria-label="关闭"
              >
                ✕
              </button>
            </div>
            <div className="overflow-y-auto px-6 py-4">
              <form onSubmit={handleCreateBug} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">BUG类型</label>
                    <select
                      value={newBug.type}
                      onChange={(e) => setNewBug({ ...newBug, type: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      required
                    >
                      <option value="">请选择类型</option>
                      {project.bugTypes.map((type) => (
                        <option key={type} value={type}>
                          {type}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">出现位置</label>
                    <select
                      value={newBug.location}
                      onChange={(e) => setNewBug({ ...newBug, location: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      required
                    >
                      <option value="">请选择位置</option>
                      {project.bugLocations.map((location) => (
                        <option key={location} value={location}>
                          {location}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">BUG描述</label>
                  <textarea
                    value={newBug.content}
                    onChange={(e) => setNewBug({ ...newBug, content: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    rows={3}
                    placeholder="请详细描述BUG的具体表现..."
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">备注</label>
                  <textarea
                    value={newBug.remark}
                    onChange={(e) => setNewBug({ ...newBug, remark: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    rows={2}
                    placeholder="其他说明信息..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">相关图片</label>
                  <div
                    ref={imageUploadRef}
                    className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-indigo-400 transition-colors cursor-pointer"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <div className="text-gray-500">
                      <div className="text-sm mb-2">📷 点击选择图片或按 Ctrl+V 粘贴截图</div>
                      <div className="text-xs text-gray-400">支持 JPG、PNG、GIF 格式，单个文件不超过 5MB</div>
                    </div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      accept="image/*"
                      onChange={handleFileSelect}
                      className="hidden"
                    />
                  </div>
                </div>
                {bugImages.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      已选择的图片 ({bugImages.length})
                    </label>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                      {bugImages.map((image) => (
                        <div key={image.id} className="relative group">
                          <img
                            src={image.url}
                            alt={image.name}
                            className="w-full h-24 object-cover rounded-lg border border-gray-200"
                          />
                          <button
                            type="button"
                            onClick={() => removeImage(image.id)}
                            className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            ×
                          </button>
                          <div className="text-xs text-gray-500 mt-1 truncate">{image.name}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <div className="flex justify-end space-x-3 pt-2">
                  <button
                    type="button"
                    onClick={closeCreateBugModal}
                    className="px-4 py-2 text-gray-700 bg-gray-200 hover:bg-gray-300 rounded-md text-sm font-medium"
                  >
                    取消
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-md text-sm font-medium"
                  >
                    创建BUG
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* 分享管理 */}
      {showShareModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">分享给客户</h3>
              <button
                type="button"
                onClick={() => setShowShareModal(false)}
                className="text-gray-400 hover:text-gray-600 text-xl leading-none"
                aria-label="关闭"
              >
                ✕
              </button>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              生成链接后可将「复制链接与分享码」整段发给客户。客户可查看与新增 BUG，不可标记完成或删除。
            </p>
            {lastCreatedShare && (
              <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-md text-sm">
                <p className="font-medium text-amber-900 mb-1">新链接已生成</p>
                <p className="text-amber-800">
                  分享码：<span className="font-mono text-lg tracking-widest">{lastCreatedShare.pin}</span>
                </p>
                <button
                  type="button"
                  onClick={() => void copyShareClipboard(lastCreatedShare.token, lastCreatedShare.pin)}
                  className="mt-2 text-indigo-600 hover:text-indigo-800 text-sm font-medium"
                >
                  复制链接与分享码
                </button>
              </div>
            )}
            <button
              type="button"
              onClick={handleCreateShareLink}
              className="w-full mb-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-md text-sm font-medium"
            >
              生成新的分享链接
            </button>
            <h4 className="text-sm font-medium text-gray-700 mb-2">已有链接</h4>
            {shareLoading ? (
              <p className="text-sm text-gray-500">加载中…</p>
            ) : shareLinks.length === 0 ? (
              <p className="text-sm text-gray-500">暂无分享链接</p>
            ) : (
              <>
                <ul className="space-y-2">
                  {pagedShareLinks.map((row) => (
                    <li
                      key={row.id}
                      className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 border border-gray-100 rounded p-2 text-sm"
                    >
                      <div>
                        <span className={row.active ? 'text-green-700' : 'text-gray-400'}>
                          {row.active ? '有效' : '已作废'}
                        </span>
                        <span className="text-gray-500 ml-2">{new Date(row.createdAt).toLocaleString()}</span>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {row.active && (
                          <>
                            <button
                              type="button"
                              onClick={() => void copyShareClipboard(row.token, row.pin)}
                              className="text-indigo-600 hover:text-indigo-800"
                            >
                              复制链接与分享码
                            </button>
                            <button
                              type="button"
                              onClick={() => handleRevokeShare(row.id)}
                              className="text-red-600 hover:text-red-800"
                            >
                              作废
                            </button>
                          </>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
                {shareLinks.length > shareLinksPageSize && (
                  <div className="mt-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 text-sm text-gray-600">
                    <span>
                      第 {(shareLinksPage - 1) * shareLinksPageSize + 1}–
                      {Math.min(shareLinksPage * shareLinksPageSize, shareLinks.length)} 条，共 {shareLinks.length} 条
                    </span>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => setShareLinksPage((p) => Math.max(1, p - 1))}
                        disabled={shareLinksPage === 1}
                        className={`px-3 py-1.5 rounded-md font-medium ${
                          shareLinksPage === 1
                            ? 'text-gray-400 bg-gray-100 cursor-not-allowed'
                            : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        上一页
                      </button>
                      <span className="text-gray-500 tabular-nums">
                        {shareLinksPage} / {shareLinksTotalPages}
                      </span>
                      <button
                        type="button"
                        onClick={() => setShareLinksPage((p) => Math.min(shareLinksTotalPages, p + 1))}
                        disabled={shareLinksPage === shareLinksTotalPages}
                        className={`px-3 py-1.5 rounded-md font-medium ${
                          shareLinksPage === shareLinksTotalPages
                            ? 'text-gray-400 bg-gray-100 cursor-not-allowed'
                            : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        下一页
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* 图片查看模态框 */}
      {showImageModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">查看图片</h3>
              <button
                onClick={() => setShowImageModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {selectedImages.map((imageUrl, index) => (
                <div key={index} className="text-center">
                  <img
                    src={imageUrl}
                    alt={`图片 ${index + 1}`}
                    className="max-w-full h-auto rounded-lg border border-gray-200"
                  />
                  <p className="text-sm text-gray-500 mt-2">图片 {index + 1}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectDetail; 